import { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Users, Calendar, UserCog, Building2,
  Truck, Package, FlaskConical, CreditCard, Settings, LogOut, Menu, X,
  ChevronDown, Globe, Bell, Stethoscope, CalendarDays, Pill, BarChart3, Shield, CalendarClock, CalendarCheck, FileText, Search
} from "lucide-react";
import Logo from "@/assets/Logo.webp";
import PatientAvatar from "@/assets/avatars/patient.jpg";

const NAV_BY_ROLE = {
  admin: [
    { path: "/dashboard", label: "dashboard", icon: LayoutDashboard },
    { path: "/analytics", label: "analytics", icon: BarChart3 },
    { path: "/doctors", label: "doctors", icon: Stethoscope },
    { path: "/schedule", label: "schedule", icon: CalendarClock },
    { path: "/patients", label: "patients", icon: Users },
    { path: "/appointments", label: "appointments", icon: Calendar },
    { path: "/calendar", label: "calendar", icon: CalendarDays },
    { path: "/staff", label: "staff", icon: UserCog },
    { path: "/departments", label: "departments", icon: Building2 },
    { path: "/vendors", label: "vendors", icon: Truck },
    { path: "/inventory", label: "inventory", icon: Package },
    { path: "/lab", label: "lab", icon: FlaskConical },
    { path: "/billing", label: "billing", icon: CreditCard },
    { path: "/insurance", label: "insurance", icon: Shield },
    { path: "/notifications", label: "notifications", icon: Bell },
    { path: "/settings", label: "settings", icon: Settings },
  ],
  doctor: [
    { path: "/dashboard", label: "dashboard", icon: LayoutDashboard },
    { path: "/appointments", label: "appointments", icon: Calendar },
    { path: "/schedule", label: "my_schedule", icon: CalendarClock },
    { path: "/patients", label: "patients", icon: Users },
    { path: "/prescriptions", label: "prescriptions", icon: Pill },
    { path: "/lab", label: "lab", icon: FlaskConical },
  ],
  receptionist: [
    { path: "/dashboard", label: "dashboard", icon: LayoutDashboard },
    { path: "/appointments", label: "appointments", icon: Calendar },
    { path: "/calendar", label: "calendar", icon: CalendarDays },
    { path: "/patients", label: "patients", icon: Users },
  ],
  lab_technician: [
    { path: "/dashboard", label: "dashboard", icon: LayoutDashboard },
    { path: "/lab", label: "lab", icon: FlaskConical },
  ],
  staff: [
    { path: "/dashboard", label: "dashboard", icon: LayoutDashboard },
  ],
  patient: [
    { path: "/dashboard", key: "dashboard", label: "Dashboard", icon: Calendar, exact: true },
    { path: "/book", key: "appointments", label: "Appointments", icon: CalendarCheck },
    { path: "/dashboard?tab=prescriptions", key: "prescriptions", label: "Prescriptions", icon: FileText, tab: "prescriptions" },
    { path: "/dashboard?tab=lab-reports", key: "lab_reports", label: "Lab Reports", icon: FlaskConical, tab: "lab-reports" },
    { path: "/dashboard?tab=billing", key: "billing", label: "Billing", icon: CreditCard, tab: "billing" },
  ],
};

const LANGS = [
  { code: "en", label: "English", short: "EN" },
  { code: "hi", label: "हिंदी", short: "HI" },
  { code: "ar", label: "العربية", short: "AR" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(3);

  const notifications = [
    {
      id: "n1",
      title: "Appointment Confirmed",
      desc: "Dr. Priya Sharma • Mon, 08 Sep 2026 at 10:30 AM (Token #3)",
      time: "10m ago",
      icon: CalendarCheck,
      color: "text-[#0c7a5f]",
      bg: "bg-[#eaf8f0]",
      tab: "appointments",
    },
    {
      id: "n2",
      title: "Lab Report Available",
      desc: "Complete Blood Count (CBC) test results are ready to download",
      time: "2h ago",
      icon: FlaskConical,
      color: "text-[#7c3aed]",
      bg: "bg-[#f5f3ff]",
      tab: "lab-reports",
    },
    {
      id: "n3",
      title: "Invoice Paid",
      desc: "Invoice #INV-2026-089 for ₹900 recorded successfully",
      time: "1d ago",
      icon: CreditCard,
      color: "text-[#ea580c]",
      bg: "bg-[#fff7ed]",
      tab: "billing",
    },
  ];

  const isPatient = user?.role === "patient";
  const navItems = NAV_BY_ROLE[user?.role] || NAV_BY_ROLE.patient;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isItemActive = (item) => {
    if (!isPatient) {
      return location.pathname === item.path;
    }
    const currentTab = new URLSearchParams(location.search).get("tab");
    if (item.tab) {
      return location.pathname === "/dashboard" && currentTab === item.tab;
    }
    if (item.exact) {
      return (location.pathname === "/dashboard" || location.pathname === "/portal") && !currentTab;
    }
    return location.pathname === item.path;
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-white justify-between">
      <div>
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
          <img
            src={Logo}
            alt="Clinic Logo"
            className="w-8 h-8 rounded-lg object-contain"
          />
          <div>
            <p className="text-base font-bold text-[#0d7d64] leading-tight font-sans tracking-tight">MedLif</p>
            <p className="text-[11px] text-slate-400 font-normal leading-none mt-0.5">Smart Clinic & Hospital Management</p>
          </div>
        </div>

        {/* Nav Items */}
        <nav className="px-3 py-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isItemActive(item);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${(item.key || item.label).toLowerCase()}`}
                className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group ${
                  active
                    ? "bg-[#eaf8f0] text-[#0c7a5f] font-semibold shadow-xs"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                <Icon size={18} className={`flex-shrink-0 ${active ? "text-[#0c7a5f]" : "text-slate-500 group-hover:text-slate-700"}`} />
                <span>{t(item.key || item.label)}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* Bottom Promo Card & User Section */}
      <div className="px-3 pb-4">
        {isPatient ? (
          <div className="p-4 rounded-2xl bg-gradient-to-br from-[#eaf8f0] via-[#f1fbf5] to-[#f8fdfa] border border-emerald-100/70 relative overflow-hidden shadow-xs">
            <div className="w-9 h-9 rounded-xl bg-white/90 border border-emerald-100 shadow-xs flex items-center justify-center text-[#0c7a5f] mb-2.5">
              <Stethoscope size={20} className="text-[#0c7a5f]" />
            </div>
            <p className="text-xs font-bold text-[#0c7a5f] leading-snug">Your Health<br />Our Priority</p>
            <p className="text-[10px] text-slate-500 mt-1 leading-normal">Better care. Healthier tomorrow.</p>
            
            <div className="absolute -bottom-2 -right-1 opacity-20 pointer-events-none">
              <svg width="55" height="55" viewBox="0 0 24 24" fill="currentColor" className="text-emerald-700">
                <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 0 0 8 20C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z"/>
              </svg>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-emerald-100 text-emerald-800 text-xs font-semibold">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role?.replace("_", " ")}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#f7faf8] overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-slate-200/80 flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <aside className="relative flex flex-col w-64 h-full bg-white shadow-xl">
            <button
              className="absolute top-4 right-4 p-1 rounded text-slate-500 hover:text-slate-900"
              onClick={() => setSidebarOpen(false)}
            >
              <X size={20} />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="bg-white/95 backdrop-blur-md border-b border-slate-200/70 px-4 md:px-6 py-2.5 flex items-center justify-between flex-shrink-0 z-10">
          <div className="flex items-center gap-3 flex-1 max-w-lg">
            <button
              className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu size={20} />
            </button>
            
            {/* Search Bar matching the design */}
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
              <input
                type="text"
                placeholder="Search doctors, tests, or anything..."
                className="w-full pl-9 pr-4 py-1.5 bg-slate-50 border border-slate-200/90 rounded-full text-xs text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Interactive Notification Bell Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="relative p-2 text-slate-600 hover:text-slate-900 rounded-full hover:bg-slate-100 transition"
                  data-testid="notifications-btn"
                >
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-rose-500 text-white rounded-full text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 p-0 rounded-2xl shadow-xl border border-slate-200/90 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-[#fbfdfc]">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-900 font-sans">Notifications</span>
                    {unreadCount > 0 && (
                      <span className="text-[10px] bg-emerald-50 text-[#0c7a5f] font-bold px-2 py-0.5 rounded-full border border-emerald-100">
                        {unreadCount} New
                      </span>
                    )}
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={() => setUnreadCount(0)}
                      className="text-[11px] font-medium text-[#0c7a5f] hover:underline"
                    >
                      Mark all read
                    </button>
                  )}
                </div>

                <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
                  {notifications.map((n) => {
                    const NIcon = n.icon;
                    return (
                      <div
                        key={n.id}
                        onClick={() => {
                          if (n.tab) navigate(`/dashboard?tab=${n.tab}`);
                        }}
                        className="p-3 hover:bg-slate-50/80 transition cursor-pointer flex items-start gap-3"
                      >
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${n.bg}`}>
                          <NIcon size={15} className={n.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-800 leading-tight">{n.title}</p>
                          <p className="text-[11px] text-slate-500 mt-0.5 leading-snug line-clamp-2">{n.desc}</p>
                          <p className="text-[10px] text-slate-400 mt-1">{n.time}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Language Selector Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 text-slate-700 hover:text-slate-900 font-medium text-xs px-2.5 rounded-full border border-slate-200/80 hover:bg-slate-50" data-testid="language-selector">
                  <Globe size={15} className="text-[#0c7a5f]" />
                  <span className="uppercase font-semibold">{language || "EN"}</span>
                  <ChevronDown size={13} className="text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-36 rounded-xl shadow-lg border border-slate-200/80 p-1">
                {LANGS.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    onClick={() => setLanguage(l.code)}
                    className={`flex items-center justify-between text-xs py-2 px-2.5 rounded-lg cursor-pointer ${
                      language === l.code ? "text-[#0c7a5f] font-bold bg-[#eaf8f0]" : "text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid={`lang-${l.code}`}
                  >
                    <span>{l.label}</span>
                    <span className="text-[10px] text-slate-400 uppercase font-mono">{l.short}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Profile Pill */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-2 pl-1.5 pr-2.5 py-1 rounded-full hover:bg-slate-100" data-testid="user-menu-btn">
                  <Avatar className="h-7 w-7 ring-1 ring-emerald-200">
                    {isPatient && <AvatarImage src={PatientAvatar} alt={user?.name || "Patient"} />}
                    <AvatarFallback className="bg-emerald-100 text-emerald-800 text-xs font-semibold">
                      {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-xs font-semibold text-slate-800">{user?.name || "Demo Patient"}</span>
                  <ChevronDown size={13} className="text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-3 py-2">
                  <p className="text-sm font-semibold text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-rose-600 focus:text-rose-700" data-testid="logout-btn">
                  <LogOut size={16} className="mr-2" />
                  {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-[#f8faf9]">
          <div className="page-enter max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
